import type { SwmmProject } from "./types.ts";

export type TraceDirection = "downstream" | "upstream";

export interface NetworkTraceResult {
  nodes: string[];
  links: string[];
}

interface TraceEdge {
  next: string;
  name: string;
}

/**
 * Indexes by uppercased name because real SWMM ids are case-insensitive, but
 * resolves each returned node name back to its own CANONICAL declared
 * casing (`project.nodes`) rather than whatever casing the link row that
 * first reached it happened to use — the map-highlight consumer (App.tsx)
 * keys its `Map<string, string>` by EXACT name, so a casing mismatch here
 * would silently fail to highlight the right node.
 */
export function traceNetwork(
  project: SwmmProject,
  startNode: string,
  direction: TraceDirection
): NetworkTraceResult {
  const canonicalName = new Map<string, string>();
  for (const node of project.nodes) canonicalName.set(node.name.toUpperCase(), node.name);

  const index = new Map<string, TraceEdge[]>();
  for (const link of project.links) {
    const from = direction === "downstream" ? link.fromNode : link.toNode;
    const to = direction === "downstream" ? link.toNode : link.fromNode;
    const key = from.toUpperCase();
    const edges = index.get(key);
    if (edges) edges.push({ next: to, name: link.name });
    else index.set(key, [{ next: to, name: link.name }]);
  }

  const visited = new Set<string>();
  const nodes: string[] = [];
  const linkNames = new Set<string>();
  const pending: string[] = [startNode];
  let head = 0;
  while (head < pending.length) {
    const node = pending[head++];
    const key = node.toUpperCase();
    if (visited.has(key)) continue;
    visited.add(key);
    nodes.push(canonicalName.get(key) ?? node);
    for (const edge of index.get(key) ?? []) {
      linkNames.add(edge.name);
      pending.push(edge.next);
    }
  }

  return { nodes, links: [...linkNames] };
}
